import { useState } from "react";

import { AuthService, type ValidateSignupKeyResponse } from "@/api";
import { ApiError } from "@/api/client";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";

interface Step2Data {
  signup_key: string;
  workspace_info: ValidateSignupKeyResponse;
}

interface RegisterStep2Props {
  initialCode: string;
  onBack: () => void;
  onNext: (data: Step2Data) => void;
}

export function RegisterStep2({
  initialCode,
  onBack,
  onNext,
}: RegisterStep2Props) {
  const [code, setCode] = useState(initialCode);
  const { t } = useTranslation();

  const { mutate, isLoading, error } = useMutation(
    (signupKey: string) => AuthService.validateSignupKey(signupKey),
    {
      onSuccess: (info) => {
        if (!info.valid) {
          // Validate endpoint returns 200 + {valid: false} for
          // unknown codes (anti-enumeration). Surface the same
          // friendly message we'd show for a 404.
          throw new ApiError(t("auth.register.step2.errors.invalidKey"), {
            status: 422,
          });
        }
        onNext({ signup_key: code.trim(), workspace_info: info });
      },
    }
  );

  return (
    <div>
      <h2 className="text-xl font-medium text-ocean-800">
        {t("auth.register.step2.title")}
      </h2>
      <p className="mt-1 text-sm text-neutral-600">
        {t("auth.register.step2.subtitle")}
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void mutate(code.trim());
        }}
        className="mt-6 space-y-4"
      >
        <FormField
          label={t("auth.register.step2.keyLabel")}
          value={code}
          onChange={setCode}
          placeholder={t("auth.register.step2.keyPlaceholder")}
          hint={t("auth.register.step2.keyHint")}
          autoComplete="one-time-code"
          required
        />

        {error && (
          <Alert variant="danger">
            {error.message.toLowerCase().includes("invalid") ||
            error.message.toLowerCase().includes("not")
              ? t("auth.register.step2.errors.invalidKey")
              : error.message}
          </Alert>
        )}

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1"
          >
            {t("auth.register.step2.backButton")}
          </Button>
          <Button type="submit" disabled={isLoading} className="flex-1">
            {isLoading
              ? t("auth.register.step2.validateLoading")
              : t("auth.register.step2.validateButton")}
          </Button>
        </div>
      </form>
    </div>
  );
}
